/* ==========================================================================
   ABO TALEB — motion engine
   One observer set for the whole page. Sections opt in with data attributes,
   so a section added in the Theme Editor animates without extra wiring.
   ========================================================================== */
(function () {
  'use strict';

  var EASE = 'cubic-bezier(.22,1,.36,1)';
  var root = document.documentElement;
  var body = document.body;

  var AT = window.AT = window.AT || {};

  /* ---------- Arabic label repair -----------------------------------------
     Several sections still ask for a Latin monospace face on their small
     labels. That face has no Arabic cut, so the browser swaps mid-word and the
     wide tracking pulls the joined letters apart. Rather than hunting the
     family name through every section file, every stylesheet on the page is
     swept once and the offending declarations are rewritten in place.      */
  var LABEL_STACK = "'IBM Plex Sans Arabic','Readex Pro',system-ui,sans-serif";

  function repairRules(rules) {
    for (var i = 0; i < rules.length; i++) {
      var rule = rules[i];
      if (rule.cssRules) { repairRules(rule.cssRules); continue; }
      if (!rule.style) continue;
      var family = rule.style.fontFamily || '';
      if (family.indexOf('Plex Mono') === -1) continue;
      rule.style.fontFamily = LABEL_STACK;
      /* Tracking past ~0.06em breaks the joins between Arabic letters. */
      var spacing = rule.style.letterSpacing || '';
      if (spacing.indexOf('em') > -1 && parseFloat(spacing) > 0.06) {
        rule.style.letterSpacing = '.04em';
      }
      if (rule.style.textTransform === 'uppercase') rule.style.textTransform = 'none';
    }
  }

  function repairLabelFont() {
    var sheets = document.styleSheets;
    for (var i = 0; i < sheets.length; i++) {
      var rules;
      try { rules = sheets[i].cssRules; } catch (e) { continue; }  /* cross-origin */
      if (rules) repairRules(rules);
    }
  }
  AT.repairLabelFont = repairLabelFont;

  /* ---------- header height ----------------------------------------------
     The header is fixed, and a spacer of the same height holds the page down.
     A wrapping announcement makes the real header taller than the setting, so
     the spacer is driven by a measurement instead of a fixed number — without
     it the first section of every page slides under the header.              */
  var headerPx = -1;
  function syncHeader() {
    var h = document.querySelector('[data-at-header]');
    if (!h) return;
    var px = Math.round(h.getBoundingClientRect().height);
    /* A ResizeObserver drives this, and every pinned section's `top` and height
       hang off the variable — so rewriting it with the value it already has
       re-lays-out those scenes for nothing, and can feed the observer back into
       itself. Only write on a real change. */
    if (px > 0 && px !== headerPx) {
      headerPx = px;
      root.style.setProperty('--at-header-h', px + 'px');
    }
  }
  AT.syncHeader = syncHeader;

  /* ---------- motion preference ------------------------------------------ */
  var KEY = 'atMotion';
  function prefersReduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  function stored() {
    try { return window.localStorage.getItem(KEY); } catch (e) { return null; }
  }
  AT.motion = function (mode) {
    if (mode) {
      try { window.localStorage.setItem(KEY, mode); } catch (e) {}
    }
    var m = mode || stored() || (prefersReduced() ? 'reduced' : 'full');
    body.classList.toggle('at-motion', m === 'full');
    document.querySelectorAll('[data-at-motion-btn]').forEach(function (b) {
      b.setAttribute('aria-pressed', String(b.getAttribute('data-at-motion-btn') === m));
    });
    if (m !== 'full') {
      /* Reduced: land every element on its finished state immediately. */
      document.querySelectorAll('[data-at-reveal],[data-at-draw],[data-at-mask]')
        .forEach(function (el) { el.classList.add('is-in'); });
    }
    return m;
  };

  /* ---------- reveal / draw / mask --------------------------------------- */
  var io = null;
  function observer() {
    if (io) return io;
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var delay = parseInt(el.getAttribute('data-at-delay') || '0', 10);
        setTimeout(function () { el.classList.add('is-in'); }, delay);
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.05 });
    return io;
  }

  function bindReveals(scope) {
    var els = (scope || document).querySelectorAll(
      '[data-at-reveal]:not([data-at-bound]),[data-at-draw]:not([data-at-bound]),[data-at-mask]:not([data-at-bound])'
    );
    els.forEach(function (el) {
      el.setAttribute('data-at-bound', '1');
      if (!body.classList.contains('at-motion')) { el.classList.add('is-in'); return; }
      observer().observe(el);
    });
  }

  /* ---------- marquee -----------------------------------------------------
     The first phrase enters from the far edge, then hands off to a seamless
     loop running at the same pixel speed so there is no visible jump.        */
  function bindMarquee(scope) {
    (scope || document).querySelectorAll('[data-at-marquee]:not([data-at-bound])').forEach(function (el) {
      el.setAttribute('data-at-bound', '1');
      var run = function () {
        var half = el.scrollWidth / 2;
        var bar = el.parentElement ? el.parentElement.clientWidth : 0;
        if (!half || !bar) return;
        var isMobile = window.matchMedia('(max-width: 749px)').matches;
        var slow = parseFloat(el.getAttribute('data-at-speed')) || 60;
        var D = isMobile ? slow * 0.7 : slow;
        el.style.setProperty('--at-enter-x', bar + 'px');
        el.style.animation = 'at-slide ' + D + 's linear infinite';
        el.style.animationDelay = '0s';
      };
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(run);
      else setTimeout(run, 600);
      var pause = el.closest('[data-at-marquee-pause]');
      if (pause) {
        var set = function (v) { return function () { el.style.animationPlayState = v; }; };
        pause.addEventListener('mouseenter', set('paused'));
        pause.addEventListener('mouseleave', set('running'));
        pause.addEventListener('touchstart', set('paused'), { passive: true });
        pause.addEventListener('touchend', set('running'), { passive: true });
      }
    });
  }

  /* ---------- parallax ---------------------------------------------------- */
  var parallaxEls = [];
  function bindParallax(scope) {
    (scope || document).querySelectorAll('[data-at-parallax]:not([data-at-bound])').forEach(function (el) {
      el.setAttribute('data-at-bound', '1');
      parallaxEls.push(el);
    });
  }
  function runParallax(vh) {
    for (var i = 0; i < parallaxEls.length; i++) {
      var el = parallaxEls[i];
      var r = el.getBoundingClientRect();
      if (r.bottom < -200 || r.top > vh + 200) continue;
      var amount = parseFloat(el.getAttribute('data-at-parallax')) || 8;
      var progress = (vh - r.top) / (vh + r.height);   /* 0 entering → 1 leaving */
      el.style.transform = 'translate3d(0,' + ((progress - 0.5) * amount * 2).toFixed(2) + '%,0)';
    }
  }

  /* ---------- hero: video + scroll scale ---------------------------------- */
  var heroes = [];
  function bindHero(scope) {
    (scope || document).querySelectorAll('[data-at-hero]:not([data-at-bound])').forEach(function (el) {
      el.setAttribute('data-at-bound', '1');
      heroes.push(el);
      var v = el.querySelector('video');
      if (v) {
        v.muted = true; v.playsInline = true; v.loop = true;   /* set before play() for iOS */
        var attempt = v.play();
        if (attempt && attempt.catch) attempt.catch(function () { /* autoplay blocked — poster stays */ });
      }
      /* Entrance: each line steps in on its own beat. */
      var items = el.querySelectorAll('[data-at-hero-el]');
      items.forEach(function (item, idx) {
        var d = 220 + idx * 170;
        if (!body.classList.contains('at-motion')) { item.classList.add('is-in'); return; }
        setTimeout(function () { item.classList.add('is-in'); }, d);
      });
    });
  }
  function runHero(vh) {
    for (var i = 0; i < heroes.length; i++) {
      var el = heroes[i];
      var media = el.querySelector('[data-at-hero-media]');
      if (!media) continue;
      var r = el.getBoundingClientRect();
      var span = r.height - vh;
      /* A fixed-ratio hero (phone) is shorter than the viewport, so there is no
         scroll travel to map — hold the media at its resting scale. */
      if (span <= 0) { media.style.transform = 'scale(1)'; continue; }
      var p = Math.min(1, Math.max(0, -r.top / span));
      media.style.transform = 'scale(' + (1.06 - p * 0.06).toFixed(4) + ')';
    }
  }

  /* ---------- story: pinned scroll sequence ------------------------------- */
  var stories = [];
  function bindStory(scope) {
    (scope || document).querySelectorAll('[data-at-story]:not([data-at-bound])').forEach(function (el) {
      el.setAttribute('data-at-bound', '1');
      stories.push({
        root: el,
        frame: el.querySelector('[data-at-story-frame]'),
        media: el.querySelectorAll('[data-at-story-media]'),
        callouts: el.querySelectorAll('[data-at-story-callout]'),
        cards: el.querySelectorAll('[data-at-story-card]'),
        ticks: el.querySelectorAll('[data-at-story-tick]')
      });
    });
  }
  /* Scroll maths ported 1:1 from the design canvas (OBJECT Homepage.dc.html →
     Runtime.story), which is the reference this section is meant to match.
     The canvas hard-codes four steps; every constant below is written as a
     fraction of one step's band so N blocks behave identically — with N = 4
     the numbers land back on the canvas's originals.

     Three things this restores that the banded version lost:
       · the frame never falls to 0 (it floors at .1 and holds once pinned),
       · images cross-fade on distance from their band centre, so neighbours
         overlap instead of hard-cutting,
       · a callout STAYS on screen once revealed — they accumulate down the
         scene rather than one blinking in and out at a time. */
  function atClamp(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }
  function atEase(t) { return 1 - Math.pow(1 - t, 3); }

  /* Only touch the DOM when a value actually changes. runStory fires on every
     scroll frame, and re-writing an unchanged opacity or transform still costs
     a style recalculation on a pinned, full-height image — which is the other
     half of the shimmer the compositor hints in the CSS address. */
  function atSet(el, prop, val) {
    var key = '__at_' + prop;
    if (el[key] === val) return;
    el[key] = val;
    el.style[prop] = val;
  }

  function runStory(vh) {
    /* On a phone the section fills the screen the moment it arrives, so the
       first frame is shown straight away instead of waiting for scroll
       progress — otherwise the reader meets an empty screen. */
    var mobile = window.innerWidth <= 899;

    for (var i = 0; i < stories.length; i++) {
      var s = stories[i];
      var sec = s.root;
      var span = sec.offsetHeight - vh;
      var p = atClamp(span > 0 ? -sec.getBoundingClientRect().top / span : 0);
      var n = s.media.length || s.callouts.length || 1;
      var band = 1 / n;

      if (s.frame) {
        var e = mobile ? 1 : atClamp(p / 0.06);
        atSet(s.frame, 'opacity', (0.1 + 0.9 * e).toFixed(3));
        atSet(s.frame, 'transform', 'scale(' + (0.94 + 0.06 * e).toFixed(4) + ')');
      }

      for (var j = 0; j < s.media.length; j++) {
        var c = (j + 0.5) * band;
        var o = atClamp(1 - (Math.abs(p - c) - 0.36 * band) / (0.28 * band));
        if (mobile && j === 0 && p < c) o = 1;
        atSet(s.media[j], 'opacity', o.toFixed(3));
      }

      /* marker → line → text, staged inside each callout's own ramp. The lead-in
         is the canvas's: the first callout waits a touch longer than the rest
         (its stops are 0.04 / 0.28 / 0.53 / 0.78 over four steps, i.e. 0.16 of a
         band for the first and 0.12 for every one after). */
      for (var k = 0; k < s.callouts.length; k++) {
        var el = s.callouts[k];
        var lead = (k === 0 ? 0.16 : 0.12) * band;
        var t = atClamp((p - (k * band + lead)) / (0.44 * band));
        atSet(el, 'opacity', t > 0 ? '1' : '0');
        var marker = el.querySelector('[data-at-c-marker]');
        var line = el.querySelector('[data-at-c-line]');
        var text = el.querySelector('[data-at-c-text]');
        if (marker) atSet(marker, 'transform', 'scale(' + atClamp(t / 0.35).toFixed(3) + ')');
        if (line) atSet(line, 'transform', 'scaleX(' + atClamp((t - 0.25) / 0.4).toFixed(3) + ')');
        if (text) {
          var tt = atEase(atClamp((t - 0.55) / 0.45));
          atSet(text, 'opacity', tt.toFixed(3));
          atSet(text, 'transform', 'translateX(' + ((1 - tt) * 70).toFixed(1) + 'px)');
        }
      }

      var active = 0;
      for (var a = 0; a < n; a++) if (p >= a * band) active = a;

      /* Mobile caption: one at a time, swapped on the step boundary. */
      for (var m = 0; m < s.cards.length; m++) {
        var on = m === active;
        atSet(s.cards[m], 'opacity', on ? '1' : '0');
        atSet(s.cards[m], 'transform', on ? 'translateY(0)' : 'translateY(12px)');
      }

      for (var q = 0; q < s.ticks.length; q++) {
        atSet(s.ticks[q], 'background',
          q <= active && p > 0.01 ? 'var(--at-ink)' : 'var(--at-cream)');
      }
    }
  }

  /* ---------- carousel (press quotes) ------------------------------------- */
  function bindCarousel(scope) {
    (scope || document).querySelectorAll('[data-at-carousel]:not([data-at-bound])').forEach(function (el) {
      el.setAttribute('data-at-bound', '1');
      var slides = el.querySelectorAll('[data-at-slide]');
      var images = el.querySelectorAll('[data-at-slide-image]');
      var dots = el.querySelectorAll('[data-at-dot]');
      var n = slides.length;
      if (!n) return;
      var index = 0, timer = null;
      var delay = (parseFloat(el.getAttribute('data-at-autoplay')) || 0) * 1000;

      function paint() {
        for (var i = 0; i < n; i++) {
          var on = i === index;
          if (slides[i]) {
            slides[i].style.opacity = on ? '1' : '0';
            slides[i].style.transform = on ? 'translateX(0)' : 'translateX(16px)';
            slides[i].setAttribute('aria-hidden', String(!on));
          }
          if (images[i]) {
            images[i].style.opacity = on ? '1' : '0';
            images[i].style.transform = on ? 'translateX(0)' : 'translateX(28px)';
          }
          if (dots[i]) {
            dots[i].style.background = on ? 'var(--at-ink)' : 'transparent';
            dots[i].setAttribute('aria-current', String(on));
          }
        }
      }
      function go(i) { index = (i + n) % n; paint(); restart(); }
      function restart() {
        if (timer) clearInterval(timer);
        if (delay > 0 && body.classList.contains('at-motion')) {
          timer = setInterval(function () { index = (index + 1) % n; paint(); }, delay);
        }
      }

      dots.forEach(function (d, i) { d.addEventListener('click', function () { go(i); }); });

      el.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { go(index + (root.dir === 'rtl' ? -1 : 1)); }
        if (e.key === 'ArrowLeft') { go(index + (root.dir === 'rtl' ? 1 : -1)); }
      });

      /* Swipe: horizontal intent only, so a vertical scroll is never hijacked. */
      var x0 = null, y0 = null;
      el.addEventListener('touchstart', function (e) {
        x0 = e.touches[0].clientX; y0 = e.touches[0].clientY;
      }, { passive: true });
      el.addEventListener('touchend', function (e) {
        if (x0 === null) return;
        var dx = e.changedTouches[0].clientX - x0;
        var dy = e.changedTouches[0].clientY - y0;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.4) {
          go(index + (dx < 0 ? 1 : -1) * (root.dir === 'rtl' ? -1 : 1));
        }
        x0 = y0 = null;
      }, { passive: true });

      el.addEventListener('mouseenter', function () { if (timer) clearInterval(timer); });
      el.addEventListener('mouseleave', restart);

      paint(); restart();
    });
  }

  /* ---------- manifesto: line-by-line focus ------------------------------- */
  var manifestos = [];
  function bindManifesto(scope) {
    (scope || document).querySelectorAll('[data-at-manifesto]:not([data-at-bound])').forEach(function (el) {
      el.setAttribute('data-at-bound', '1');
      manifestos.push(el);
    });
  }
  function runManifesto(vh) {
    for (var i = 0; i < manifestos.length; i++) {
      var lines = manifestos[i].querySelectorAll('[data-at-line]');
      for (var j = 0; j < lines.length; j++) {
        var r = lines[j].getBoundingClientRect();
        var mid = r.top + r.height / 2;
        var d = Math.abs(mid - vh * 0.5) / (vh * 0.5);
        var lit = Math.max(0.16, 1 - d * 1.1);
        lines[j].style.opacity = String(lit);
        lines[j].style.transform = 'translateY(' + ((1 - lit) * 22).toFixed(1) + 'px)';
      }
    }
  }

  /* ---------- viewport height ---------------------------------------------
     Every pinned scene is sized in CSS `vh` (the story is 500vh), and the
     progress maths divides by "one viewport". Those two have to be the SAME
     viewport. `window.innerHeight` is not: on iOS and Android the browser
     collapses its toolbar as you scroll, so innerHeight shrinks mid-gesture
     while CSS `vh` — and therefore the section's own height — stays put.

     Reading it every frame made the scene's span jump by the toolbar's height
     on every scroll gesture. Over the story's ~3600px span an ~85px jump moves
     progress by ~2%, and because a cross-fade covers its whole range in 7% of
     progress, that lands as up to a third of an opacity step — the image
     visibly trembling while you scroll.

     So measure what CSS means by 100vh with a probe, and re-measure only when
     the width actually changes (a toolbar collapse never changes it).         */
  var vhProbe = null, vhCache = 0, vhWidth = -1;
  function sceneVh() {
    if (vhCache && window.innerWidth === vhWidth) return vhCache;
    if (!vhProbe) {
      vhProbe = document.createElement('div');
      vhProbe.setAttribute('aria-hidden', 'true');
      vhProbe.style.cssText = 'position:absolute;top:0;left:0;width:0;height:100vh;' +
        'visibility:hidden;pointer-events:none';
      document.body.appendChild(vhProbe);
    }
    vhWidth = window.innerWidth;
    vhCache = vhProbe.offsetHeight || window.innerHeight;
    return vhCache;
  }
  function resetVh() { vhCache = 0; }

  /* ---------- scroll loop -------------------------------------------------
     One rAF-throttled pass drives every scroll-linked effect on the page.    */
  var ticking = false;
  function frame() {
    ticking = false;
    var vh = sceneVh();
    runHero(vh);
    runStory(vh);
    runManifesto(vh);
    runParallax(vh);
  }
  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(frame);
  }

  /* ---------- header state ------------------------------------------------ */
  function bindHeader() {
    var h = document.querySelector('[data-at-header]');
    if (!h) return;
    window.addEventListener('scroll', function () {
      var y = window.scrollY || window.pageYOffset;
      h.classList.toggle('is-stuck', y > 12);
    }, { passive: true });
    if (window.ResizeObserver) {
      new ResizeObserver(syncHeader).observe(h);
    }
  }

  /* ---------- boot -------------------------------------------------------- */
  AT.bind = function (scope) {
    bindReveals(scope);
    bindMarquee(scope);
    bindParallax(scope);
    bindHero(scope);
    bindStory(scope);
    bindCarousel(scope);
    bindManifesto(scope);
    repairLabelFont();
    syncHeader();
    onScroll();
  };

  function boot() {
    AT.motion();
    repairLabelFont();
    syncHeader();
    AT.bind(document);
    bindHeader();
    document.querySelectorAll('[data-at-motion-btn]').forEach(function (b) {
      b.addEventListener('click', function () {
        AT.motion(b.getAttribute('data-at-motion-btn'));
        AT.bind(document);
      });
    });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () { syncHeader(); onScroll(); }, { passive: true });
    window.addEventListener('orientationchange', function () { resetVh(); syncHeader(); onScroll(); }, { passive: true });
    window.addEventListener('load', function () { repairLabelFont(); syncHeader(); });
    /* Webfonts change the announcement's line count, so measure again after them. */
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(syncHeader);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* Theme Editor: a re-rendered section needs its handlers back. */
  document.addEventListener('shopify:section:load', function (e) {
    /* Drop stale references to nodes the editor just replaced. */
    parallaxEls = parallaxEls.filter(function (el) { return document.contains(el); });
    heroes = heroes.filter(function (el) { return document.contains(el); });
    stories = stories.filter(function (s) { return document.contains(s.root); });
    manifestos = manifestos.filter(function (el) { return document.contains(el); });
    AT.bind(e.target);
    bindHeader();
  });
})();
